import { useEffect, useState } from "react";
import axios from "axios";
import { io } from "socket.io-client";

const API_URL = process.env.REACT_APP_API_URL;
const SOCKET_URL = process.env.REACT_APP_SOCKET_URL;

const socket = io(SOCKET_URL);

function CustomerDisplay() {
  const [shop, setShop] = useState(null);

  const [displayData, setDisplayData] = useState({
    items: [],
    total: 0,
    paymentMethod: "cash",
    status: "waiting",
  });

  const token = localStorage.getItem("token")?.trim();

  useEffect(() => {
    const loadShop = async () => {
      try {
        const res = await axios.get(`${API_URL}/shop`, {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        setShop(res.data);
      } catch (error) {
        setShop({
          shopName: "African Grocery POS",
          address: "",
          phone: "",
          website: "",
        });
      }
    };

    loadShop();
  }, [token]);

  useEffect(() => {
    socket.on("cart:update", (data) => {
      setDisplayData(data);
    });

    socket.on("sale:completed", (data) => {
      setDisplayData(data);

      setTimeout(() => {
        socket.emit("display:reset");
      }, 5000);
    });

    return () => {
      socket.off("cart:update");
      socket.off("sale:completed");
    };
  }, []);

  const { items, total, paymentMethod, status, receiptNumber } = displayData;

  return (
    <div style={styles.page}>
      <div style={styles.brandBox}>
        {shop?.logoUrl && (
          <img src={shop.logoUrl} alt={shop.shopName} style={styles.logo} />
        )}

        <h1 style={styles.brand}>{shop?.shopName || "African Grocery POS"}</h1>

        {shop?.address && <p style={styles.tagline}>{shop.address}</p>}

        <p style={styles.smallBrandText}>
          {shop?.phone && <>Tel: {shop.phone}</>}
          {shop?.phone && shop?.website && " • "}
          {shop?.website && <>{shop.website}</>}
        </p>
      </div>

      {status === "completed" ? (
        <div style={styles.thankYouBox}>
          <div style={styles.checkmark}>✓</div>
          <h2 style={styles.thankYou}>Thank you for shopping with us</h2>
          <p style={styles.receipt}>Receipt: {receiptNumber}</p>
          <h1 style={styles.finalTotal}>£{Number(total || 0).toFixed(2)}</h1>
        </div>
      ) : (
        <div style={styles.mainCard}>
          <h2 style={styles.sectionTitle}>Your Basket</h2>

          {items.length === 0 ? (
            <div style={styles.emptyState}>
              <h2>Welcome</h2>
              <p>Your items will appear here as they are scanned.</p>
            </div>
          ) : (
            <div style={styles.itemsList}>
              {items.map((item) => (
                <div key={item.productId} style={styles.itemRow}>
                  <div>
                    <h3 style={styles.itemName}>{item.name}</h3>
                    <p style={styles.itemMeta}>
                      {item.quantity} {item.unitLabel || "unit"} × £
                      {Number(item.sellingPrice || 0).toFixed(2)}
                    </p>
                  </div>

                  <strong style={styles.itemTotal}>
                    £{Number(item.sellingPrice * item.quantity).toFixed(2)}
                  </strong>
                </div>
              ))}
            </div>
          )}

          <div style={styles.totalBar}>
            <div>
              <p style={styles.paymentText}>Payment: {paymentMethod}</p>
              <p style={styles.smallText}>Please check your items</p>
            </div>

            <h1 style={styles.total}>£{Number(total || 0).toFixed(2)}</h1>
          </div>
        </div>
      )}

      <style>
        {`
          @keyframes popIn {
            0% { transform: scale(0.8); opacity: 0; }
            100% { transform: scale(1); opacity: 1; }
          }

          @keyframes pulse {
            0% { transform: scale(1); }
            50% { transform: scale(1.05); }
            100% { transform: scale(1); }
          }
        `}
      </style>
    </div>
  );
}

const styles = {
 page: {
  minHeight: "100vh",
  padding: "10px",
  background: "linear-gradient(135deg, #07111f, #14213d)",
  color: "white",
  fontFamily: "Arial, sans-serif",
  boxSizing: "border-box",
  overflow: "hidden",
},
brandBox: {
  textAlign: "center",
  marginBottom: "10px",
},
logo: {
  maxWidth: "45px",
  maxHeight: "45px",
  objectFit: "contain",
  marginBottom: "6px",
},
brand: {
  fontSize: "22px",
  margin: 0,
},
tagline: {
  fontSize: "12px",
  opacity: 0.85,
  marginTop: "4px",
},
smallBrandText: {
  fontSize: "11px",
  opacity: 0.75,
  marginTop: "4px",
},
mainCard: {
  width: "100%",
  maxWidth: "520px",
  margin: "0 auto",
  background: "rgba(255,255,255,0.1)",
  borderRadius: "14px",
  padding: "12px",
  boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
  boxSizing: "border-box",
},
sectionTitle: {
  fontSize: "20px",
  margin: "0 0 10px",
},
emptyState: {
  minHeight: "140px",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  alignItems: "center",
  opacity: 0.75,
  textAlign: "center",
},
itemsList: {
  maxHeight: "220px",
  overflowY: "auto",
},
itemRow: {
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
  padding: "8px 0",
  borderBottom: "1px solid rgba(255,255,255,0.15)",
  gap: "10px",
},
itemName: {
  fontSize: "16px",
  margin: "0 0 3px 0",
},
itemMeta: {
  fontSize: "12px",
  opacity: 0.75,
  margin: 0,
},
itemTotal: {
  fontSize: "16px",
},
totalBar: {
  marginTop: "12px",
  padding: "12px",
  background: "rgba(255,255,255,0.15)",
  borderRadius: "12px",
  display: "flex",
  justifyContent: "space-between",
  alignItems: "center",
},
paymentText: {
  fontSize: "13px",
  margin: 0,
  textTransform: "capitalize",
},
smallText: {
  margin: "3px 0 0",
  opacity: 0.7,
  fontSize: "11px",
},
total: {
  fontSize: "34px",
  margin: 0,
},
};

export default CustomerDisplay;